/**
 * Thin fetch wrapper. No axios - same-origin fetch already does
 * everything needed here. Centralizes:
 *   - credentials: 'same-origin' so the existing httpOnly session
 *     cookie is always sent (this is the ONLY thing carrying auth -
 *     no token is ever handled in JS, by design)
 *   - JSON request/response handling
 *   - a single, consistent error shape components can rely on
 *   - redirecting to /auth/login on a 401 from ANY call, so no
 *     individual screen has to think about session expiry
 */

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...options,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (res.status === 401 && !path.startsWith('/auth/me')) {
    // TEMPORARY DIAGNOSTIC (remove once the redirect-to-dashboard issue is found) -
    // logs the exact failing request to the server console via a public
    // beacon, so it's visible in `az webapp log tail` without needing
    // browser DevTools. sendBeacon sends a plain string as text/plain,
    // NOT application/json, so express.json() would silently skip
    // parsing it - an explicit Blob with the right MIME type is
    // required for the server to actually see this payload.
    try {
      const diagBlob = new Blob([JSON.stringify({ path, currentUrl: window.location.href })], { type: 'application/json' });
      navigator.sendBeacon('/api/diag/client-401', diagBlob);
    } catch {
      // best-effort only - never block the real redirect on this
    }
    // Session expired or never existed - the existing server-side
    // Entra ID flow is the only way back in. Full navigation, not a
    // fetch, since Microsoft's sign-in requires top-level navigation.
    window.location.href = '/auth/login';
    // Never actually resolves after this - navigation is already happening.
    return new Promise<T>(() => {});
  }

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const message = body?.error || `Request failed with status ${res.status}`;
    throw new ApiError(message, res.status);
  }

  return body as T;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
